import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Button, Alert, Space, Modal, Input, theme } from 'antd';
import { message } from '../services/toastMessage';
import { IconMapPin, IconAlertTriangle, IconPencil } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import GeofenceMap from './GeofenceMap';
import GeofenceZoneEditor, {
  DEFAULT_RADIUS_METRES,
  FALLBACK_LATITUDE,
  FALLBACK_LONGITUDE,
  isGeometryComplete,
  toGeometry,
  translatePoints,
} from './GeofenceZoneEditor';
import {
  getGeofenceZone,
  saveGeofenceZone,
  getGeofenceEvents,
  getGeofenceMoveRequests,
  requestGeofenceMove,
  decideGeofenceMove,
} from '../services/GeofenceService';
import { getHeavyDutyMachineryById } from '../services/HeavyDutyMachineryService';
import { onTelemetryUpdate } from '../services/RealtimeService';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { IGeofenceZone, IGeofenceEvent, IGeofenceMoveRequest } from '../interfaces/heavyDutyMachinery/IGeofence';
import { IFullMachineryTelemetry, isPublicMachineryTelemetry } from '../interfaces/heavyDutyMachinery/ITelemetry';
import { distanceMetres } from '../helpers/geoCircle';
import { TEAL, CORAL } from '../theme/creamyTokens';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatShortDateTime } from '../helpers/dateFormatHelper';

const { Title, Text } = Typography;
const { TextArea } = Input;

export interface IBookingGeofenceCardProps {
  booking: IBooking;
  isHost: boolean;
}

/**
 * The work zone, on the booking both sides already open.
 *
 * The host draws it and the guest reads it -- the same card, two different
 * sets of controls. The guest's half is not a courtesy: being able to be
 * accused of taking a machine out of its zone without being allowed to see
 * where the zone is would be indefensible, and they are the one who marked
 * the site in the first place.
 */
const BookingGeofenceCard: React.FC<IBookingGeofenceCardProps> = ({ booking, isHost }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const [zone, setZone] = useState<IGeofenceZone | undefined>(undefined);
  const [events, setEvents] = useState<IGeofenceEvent[]>([]);
  const [moveRequests, setMoveRequests] = useState<IGeofenceMoveRequest[]>([]);
  const [telemetry, setTelemetry] = useState<IFullMachineryTelemetry | undefined>(undefined);

  // The draft only exists while the host is editing. Everything else on the
  // screen keeps reading `zone`, so an abandoned edit leaves no trace.
  const [draft, setDraft] = useState<IGeofenceZone | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  // The guest's proposed zone, while they are proposing it. Same shape as the
  // host's draft, and edited with the same editor -- a move request becomes
  // the zone the moment it is approved, so the two must be able to say
  // exactly the same things.
  const [moveDraft, setMoveDraft] = useState<IGeofenceZone | undefined>(undefined);
  const [moveReason, setMoveReason] = useState('');
  const [moveSending, setMoveSending] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | undefined>(undefined);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(() => {
    Promise.all([getGeofenceZone(booking.id), getGeofenceEvents(booking.id), getGeofenceMoveRequests(booking.id)])
      .then(([loadedZone, loadedEvents, loadedRequests]) => {
        setZone(loadedZone);
        setEvents(loadedEvents);
        setMoveRequests(loadedRequests);
      })
      .catch(() => undefined);
  }, [booking.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Same two-step as ActiveRentalCard: whatever the backend already has
  // cached, then live updates over the hub.
  useEffect(() => {
    let cancelled = false;
    getHeavyDutyMachineryById(booking.heavyDutyMachineryId).then((machine) => {
      if (cancelled || !machine?.telemetry) return;
      if (!isPublicMachineryTelemetry(machine.telemetry)) setTelemetry(machine.telemetry);
    });
    return () => {
      cancelled = true;
    };
  }, [booking.heavyDutyMachineryId]);

  useEffect(
    () =>
      onTelemetryUpdate((payload: IFullMachineryTelemetry) => {
        if (payload.machineryId === booking.heavyDutyMachineryId) setTelemetry(payload);
      }),
    [booking.heavyDutyMachineryId],
  );

  const shown = draft ?? zone;
  const isEditing = draft !== undefined;
  const pendingMove = moveRequests.find((request) => request.status === 'Pending');

  // A pending request, in the shape the map speaks. Only the geometry is
  // real here: the flags exist because the map takes a zone, and a request
  // is not one.
  const pendingMoveZone: IGeofenceZone | undefined = pendingMove && {
    id: pendingMove.id,
    bookingId: pendingMove.bookingId,
    kind: pendingMove.kind,
    centerLatitude: pendingMove.centerLatitude,
    centerLongitude: pendingMove.centerLongitude,
    radiusMetres: pendingMove.radiusMetres,
    points: pendingMove.points,
    isBreached: false,
    isSuggestion: true,
    createdAt: pendingMove.createdAt,
  };

  // Only between handover and return, mirroring the backend's own gate: before
  // the machine leaves the yard it is "outside" every site in the world.
  const isWatched = !!booking.handedOverAt && !booking.returnedAt;

  const startEditing = () => {
    if (shown) {
      setDraft({ ...shown });
      return;
    }
    // No zone and no suggestion to start from: drop the host on the machine's
    // last known position rather than on an empty ocean.
    setDraft({
      id: '',
      bookingId: booking.id,
      kind: 'Circle',
      centerLatitude: telemetry?.latitude ?? FALLBACK_LATITUDE,
      centerLongitude: telemetry?.longitude ?? FALLBACK_LONGITUDE,
      radiusMetres: DEFAULT_RADIUS_METRES,
      points: [],
      isBreached: false,
      isSuggestion: true,
      createdAt: new Date().toISOString(),
    });
  };

  const handleSave = async () => {
    if (!draft || !isGeometryComplete(draft)) return;
    setSaving(true);
    try {
      const saved = await saveGeofenceZone(booking.id, toGeometry(draft));
      setZone(saved);
      setDraft(undefined);
      message.success(translate(t, TRANSLATIONS.GEOFENCE_SAVED));
    } catch {
      message.error(translate(t, TRANSLATIONS.GEOFENCE_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  /**
   * The proposal opens as the zone the host already agreed to, moved to where
   * the machine actually is -- same shape, same size, new place. That is the
   * ask in the overwhelming majority of cases, and it means the guest starts
   * from something rather than from a blank map. They can redraw it, or
   * switch shape entirely, if the new plot is nothing like the old one.
   */
  const startMoveRequest = (current: IGeofenceZone) => {
    const latitude = telemetry?.latitude ?? current.centerLatitude ?? FALLBACK_LATITUDE;
    const longitude = telemetry?.longitude ?? current.centerLongitude ?? FALLBACK_LONGITUDE;

    setMoveDraft(
      current.kind === 'Polygon'
        ? { ...current, points: translatePoints(current.points, latitude, longitude) }
        : {
            ...current,
            centerLatitude: latitude,
            centerLongitude: longitude,
            radiusMetres: current.radiusMetres ?? DEFAULT_RADIUS_METRES,
          },
    );
  };

  const closeMoveRequest = () => {
    setMoveDraft(undefined);
    setMoveReason('');
  };

  const handleRequestMove = async () => {
    if (!moveDraft || !moveReason.trim() || !isGeometryComplete(moveDraft)) return;
    setMoveSending(true);
    try {
      await requestGeofenceMove(booking.id, toGeometry(moveDraft), moveReason.trim());
      closeMoveRequest();
      message.success(translate(t, TRANSLATIONS.GEOFENCE_MOVE_SENT));
      load();
    } catch {
      message.error(translate(t, TRANSLATIONS.GEOFENCE_MOVE_ERROR));
    } finally {
      setMoveSending(false);
    }
  };

  const handleDecide = async (id: string, approve: boolean, reason?: string) => {
    try {
      await decideGeofenceMove(booking.id, id, approve, reason);
      message.success(translate(t, approve ? TRANSLATIONS.GEOFENCE_MOVE_APPROVED : TRANSLATIONS.GEOFENCE_MOVE_REJECTED));
      setRejectingId(undefined);
      setRejectReason('');
      load();
    } catch {
      message.error(translate(t, TRANSLATIONS.LIFECYCLE_ACTION_ERROR));
    }
  };

  const metresFromCenter =
    shown?.centerLatitude != null && shown?.centerLongitude != null && telemetry?.latitude != null && telemetry?.longitude != null
      ? Math.round(distanceMetres(shown.centerLatitude, shown.centerLongitude, telemetry.latitude, telemetry.longitude))
      : undefined;

  return (
    <div
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 18,
        padding: 16,
        background: token.colorBgContainer,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <IconMapPin size={18} color={TEAL} />
        <Title level={5} style={{ margin: 0 }}>
          {translate(t, TRANSLATIONS.GEOFENCE_TITLE)}
        </Title>
      </div>

      {/* The one thing worth interrupting for. Nothing else on this card
          matters while the machine is somewhere it should not be. */}
      {zone?.isBreached && (
        <Alert
          type="error"
          showIcon
          icon={<IconAlertTriangle size={18} />}
          style={{ marginBottom: 12 }}
          message={translate(t, TRANSLATIONS.GEOFENCE_BREACHED_TITLE)}
          description={
            zone.breachOpenedAt
              ? translate(t, TRANSLATIONS.GEOFENCE_BREACHED_SINCE, {
                  since: formatShortDateTime(zone.breachOpenedAt, i18n.language),
                })
              : undefined
          }
        />
      )}

      <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
        {shown
          ? isHost
            ? translate(t, isEditing || shown.isSuggestion ? TRANSLATIONS.GEOFENCE_SUGGESTION_HINT : TRANSLATIONS.GEOFENCE_HOST_HINT)
            : translate(t, TRANSLATIONS.GEOFENCE_GUEST_HINT)
          : translate(t, isHost ? TRANSLATIONS.GEOFENCE_NONE_HOST : TRANSLATIONS.GEOFENCE_NONE_GUEST)}
      </Text>

      {draft ? (
        <GeofenceZoneEditor
          draft={draft}
          onChange={setDraft}
          machineLatitude={telemetry?.latitude}
          machineLongitude={telemetry?.longitude}
          // A host redrawing the zone by hand while somebody is waiting on a
          // move still gets to see what they were asked for.
          referenceZone={pendingMoveZone}
          referenceHint={translate(t, TRANSLATIONS.GEOFENCE_MOVE_PENDING_DASHED)}
        />
      ) : (
        shown && (
          <GeofenceMap
            zone={shown}
            // The host decides on a move by looking at it, not by reading a
            // sentence about it: the requested shape is drawn dashed, on the
            // same map, next to the one it would replace.
            referenceZone={pendingMoveZone}
            machineLatitude={telemetry?.latitude}
            machineLongitude={telemetry?.longitude}
            isBreached={zone?.isBreached}
          />
        )
      )}

      {/* Where the machine is relative to the line, in words, for whoever is
          not going to squint at a map on a phone. */}
      {shown && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
          {telemetry?.latitude == null
            ? translate(t, TRANSLATIONS.GEOFENCE_NO_POSITION)
            : metresFromCenter != null
              ? `${zone?.isBreached ? '' : `${translate(t, TRANSLATIONS.GEOFENCE_INSIDE)} · `}${translate(t, TRANSLATIONS.GEOFENCE_DISTANCE_FROM_CENTER, { metres: metresFromCenter })}`
              : // A polygon has no centre, so there is no distance to give --
                // only which side of the line the machine is on.
                zone?.isBreached
                ? ''
                : translate(t, TRANSLATIONS.GEOFENCE_INSIDE)}
        </Text>
      )}

      {!isWatched && shown && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {translate(t, TRANSLATIONS.GEOFENCE_ONLY_WHILE_OUT)}
        </Text>
      )}

      {/* ---- A move somebody is waiting on ---- */}
      {pendingMove && (
        <div
          style={{
            border: `1px solid ${token.colorWarningBorder}`,
            background: token.colorWarningBg,
            borderRadius: 12,
            padding: '10px 14px',
            marginTop: 12,
          }}
        >
          <Text strong style={{ fontSize: 13, display: 'block' }}>
            {translate(t, isHost ? TRANSLATIONS.GEOFENCE_MOVE_PENDING_HOST : TRANSLATIONS.GEOFENCE_MOVE_PENDING_GUEST)}
          </Text>
          <Text style={{ fontSize: 13, display: 'block', marginTop: 2 }}>{pendingMove.reason}</Text>
          {!isEditing && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {translate(t, isHost ? TRANSLATIONS.GEOFENCE_MOVE_PENDING_DASHED : TRANSLATIONS.GEOFENCE_MOVE_REQUESTED_DASHED)}
            </Text>
          )}
          {isHost && (
            <Space style={{ marginTop: 10 }} wrap>
              <Button size="small" type="primary" onClick={() => handleDecide(pendingMove.id, true)}>
                {translate(t, TRANSLATIONS.GEOFENCE_MOVE_APPROVE)}
              </Button>
              <Button size="small" danger onClick={() => setRejectingId(pendingMove.id)}>
                {translate(t, TRANSLATIONS.GEOFENCE_MOVE_REJECT)}
              </Button>
            </Space>
          )}
        </div>
      )}

      {/* ---- Actions ---- */}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        {isHost ? (
          isEditing ? (
            <>
              <Button shape="round" type="primary" loading={saving} disabled={!draft || !isGeometryComplete(draft)} onClick={handleSave} style={{ flex: 1 }}>
                {translate(t, TRANSLATIONS.GEOFENCE_SAVE_BUTTON)}
              </Button>
              <Button shape="round" onClick={() => setDraft(undefined)}>
                {translate(t, TRANSLATIONS.GEOFENCE_CANCEL_BUTTON)}
              </Button>
            </>
          ) : (
            <Button shape="round" block icon={<IconPencil size={16} />} onClick={startEditing}>
              {translate(t, zone && !zone.isSuggestion ? TRANSLATIONS.GEOFENCE_EDIT_BUTTON : TRANSLATIONS.GEOFENCE_SAVE_BUTTON)}
            </Button>
          )
        ) : (
          // Only while the machine is actually out: before handover there is
          // nothing to move, and afterwards there is nothing to move it to.
          zone &&
          !zone.isSuggestion &&
          isWatched &&
          !pendingMove && (
            <Button shape="round" block onClick={() => startMoveRequest(zone)}>
              {translate(t, TRANSLATIONS.GEOFENCE_MOVE_REQUEST_BUTTON)}
            </Button>
          )
        )}
      </div>

      {/* ---- What has already happened ---- */}
      {events.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
            {translate(t, TRANSLATIONS.GEOFENCE_EVENTS_TITLE)}
          </Text>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {events.map((event) => (
              <div key={event.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatShortDateTime(event.timestampUtc, i18n.language)} ·{' '}
                  {translate(t, TRANSLATIONS.GEOFENCE_EVENT_MAX, { metres: Math.round(event.maxMetresOutside) })}
                </Text>
                <Text style={{ fontSize: 12, color: event.resolvedAt ? undefined : CORAL }}>
                  {translate(t, event.resolvedAt ? TRANSLATIONS.GEOFENCE_EVENT_RESOLVED : TRANSLATIONS.GEOFENCE_EVENT_OPEN)}
                </Text>
              </div>
            ))}
          </Space>
        </div>
      )}

      <Modal
        open={moveDraft !== undefined}
        title={translate(t, TRANSLATIONS.GEOFENCE_MOVE_MODAL_TITLE)}
        onCancel={closeMoveRequest}
        onOk={handleRequestMove}
        width={520}
        okText={translate(t, TRANSLATIONS.GEOFENCE_MOVE_SEND)}
        okButtonProps={{
          loading: moveSending,
          disabled: !moveReason.trim() || !moveDraft || !isGeometryComplete(moveDraft),
        }}
      >
        {moveDraft && (
          <GeofenceZoneEditor
            draft={moveDraft}
            onChange={setMoveDraft}
            machineLatitude={telemetry?.latitude}
            machineLongitude={telemetry?.longitude}
            // The zone as it stands, dashed underneath: what is being asked
            // for only means something next to what is there now.
            referenceZone={zone}
            referenceHint={translate(t, TRANSLATIONS.GEOFENCE_MOVE_CURRENT_DASHED)}
            height={220}
          />
        )}

        <Text style={{ fontSize: 13, display: 'block', marginTop: 14, marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.GEOFENCE_MOVE_REASON_LABEL)}
        </Text>
        <TextArea
          rows={3}
          value={moveReason}
          onChange={(e) => setMoveReason(e.target.value)}
          placeholder={translate(t, TRANSLATIONS.GEOFENCE_MOVE_REASON_PLACEHOLDER)}
          maxLength={500}
        />
      </Modal>

      <Modal
        open={rejectingId !== undefined}
        title={translate(t, TRANSLATIONS.GEOFENCE_MOVE_REJECT_MODAL)}
        onCancel={() => setRejectingId(undefined)}
        onOk={() => rejectingId && handleDecide(rejectingId, false, rejectReason.trim())}
        okText={translate(t, TRANSLATIONS.GEOFENCE_MOVE_REJECT)}
        okButtonProps={{ danger: true, disabled: !rejectReason.trim() }}
      >
        <TextArea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} maxLength={500} />
      </Modal>
    </div>
  );
};

export default BookingGeofenceCard;
